// ─── LightsToggle ──────────────────────────────────────────────────────────
// Small auto-hiding pill that exits Cinema Mode. Mounted by any surface
// that wraps itself in `.cinema-mode` (Video Studio, Deskeynomics, etc.)
//
// Behaviour (per design_guidelines.json):
//   • Visible on mount.
//   • Fades to opacity 0 after 3 s of inactivity.
//   • Re-appears on mousemove / keydown / touchstart.
//   • Glassmorphism pill, Apple-amber accent dot, ALL CAPS micro-label.
import React, { useEffect, useRef, useState } from 'react';
import { Sun } from 'lucide-react';

const IDLE_MS = 3000;

export default function LightsToggle({ onExit }) {
  const [visible, setVisible] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    const reveal = () => {
      setVisible(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setVisible(false), IDLE_MS);
    };
    // initial idle countdown
    timer.current = setTimeout(() => setVisible(false), IDLE_MS);

    window.addEventListener('mousemove',  reveal);
    window.addEventListener('keydown',    reveal);
    window.addEventListener('touchstart', reveal, { passive: true });
    return () => {
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener('mousemove',  reveal);
      window.removeEventListener('keydown',    reveal);
      window.removeEventListener('touchstart', reveal);
    };
  }, []);

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] transition-opacity duration-500"
      style={{
        opacity:       visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
      }}>
      <button
        data-testid="lights-toggle-button"
        onClick={onExit}
        onMouseEnter={() => setVisible(true)}
        aria-label="Turn lights on (exit cinema mode)"
        className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold transition-colors"
        style={{
          background:     'rgba(255,255,255,0.10)',
          color:          '#F5F5F7',
          border:         '1px solid rgba(255,255,255,0.16)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow:      '0 12px 32px rgba(0,0,0,0.45)',
        }}>
        <Sun className="w-3.5 h-3.5 transition-colors"
          style={{ color: '#F08A24' }} />
        Lights on
      </button>
    </div>
  );
}
